import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, ChevronLeft, ChevronRight, Compass, Search } from 'lucide-react';
import B2BLayout from '../../../components/b2b/B2BLayout';
import B2BPageShell from '../../../components/b2b/B2BPageShell';
import { ErrorBanner, PersonaAvatar, PersonaTagChip } from '../components/PersonaShared';
import { personaApi, personaAssetUrl } from '../services/personaService';
import '../styles/persona.scss';

const FILTER_ALL = 'all';
const PAGE_SIZE = 6;

function getExploreProfile(persona) {
  const sourceData = persona?.sourceData || persona?.source_data || {};
  const identity = sourceData.identity || {};
  const uxLens = sourceData.ux_lens || {};
  const domainNotes = sourceData.domain_notes || {};
  return {
    age: persona?.age,
    gender: persona?.gender,
    job: persona?.title || persona?.roleArea || persona?.role_area,
    tag: persona?.tag || identity.tag || '',
    domainTag: sourceData.domainTag || sourceData.domain_tag || identity.domain_tag || identity.domainTag || '',
    personality: persona?.personality || '',
    behavior: persona?.behaviours || persona?.behaviors || uxLens.decision_style || '',
    biography: persona?.biography || uxLens.success_looks_like || '',
    notice: Array.isArray(uxLens.what_they_notice) ? uxLens.what_they_notice.slice(0, 2) : [],
    domainHint: domainNotes.mobility_context || domainNotes.vehicle_usage || '',
  };
}

function getPaginationItems(page, totalPages) {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }).map((_, index) => index + 1);
  }

  const pages = new Set([1, totalPages, page, page - 1, page + 1]);
  const orderedPages = Array.from(pages)
    .filter((item) => item >= 1 && item <= totalPages)
    .sort((a, b) => a - b);

  return orderedPages.reduce((items, item, index) => {
    if (index > 0 && item - orderedPages[index - 1] > 1) {
      items.push(`ellipsis-${orderedPages[index - 1]}-${item}`);
    }
    items.push(item);
    return items;
  }, []);
}

function ExplorePagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  const items = getPaginationItems(page, totalPages);

  return (
    <nav className="persona-list-pagination persona-explore-pagination" aria-label="Explore 페이지">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        aria-label="이전 페이지"
      >
        <ChevronLeft size={16} />
      </button>
      <div>
        {items.map((item) => (
          typeof item === 'number' ? (
            <button
              key={item}
              type="button"
              className={item === page ? 'active' : ''}
              onClick={() => onChange(item)}
              aria-current={item === page ? 'page' : undefined}
            >
              {item}
            </button>
          ) : (
            <span key={item}>...</span>
          )
        ))}
      </div>
      <button
        type="button"
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
        aria-label="다음 페이지"
      >
        <ChevronRight size={16} />
      </button>
    </nav>
  );
}

function ExplorePersonasPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialExploreState = location.state?.exploreState || {};
  const [personas, setPersonas] = useState([]);
  const [facets, setFacets] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState(() => initialExploreState.activeFilter || FILTER_ALL);
  const [query, setQuery] = useState(() => initialExploreState.query || '');
  const [debouncedQuery, setDebouncedQuery] = useState(() => initialExploreState.query || '');
  const [page, setPage] = useState(() => Math.max(1, Number(initialExploreState.page) || 1));

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [activeFilter, debouncedQuery]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await personaApi.listExplorePersonas({
        page,
        limit: PAGE_SIZE,
        search: debouncedQuery || undefined,
        tag: activeFilter !== FILTER_ALL ? activeFilter : undefined,
      });
      setPersonas(Array.isArray(result?.data) ? result.data : []);
      setFacets(Array.isArray(result?.facets?.tags) ? result.facets.tags : []);
      const nextPagination = result?.pagination || {};
      const total = Number(nextPagination.total || 0);
      const limit = Number(nextPagination.limit || PAGE_SIZE);
      const totalPages = Number(
        nextPagination.total_pages
        || nextPagination.totalPages
        || Math.max(1, Math.ceil(total / limit) || 1),
      );
      setPagination({
        page: Number(nextPagination.page || page),
        limit,
        total,
        totalPages,
      });
    } catch (err) {
      setError(err?.message || 'Explore 퍼소나를 불러오지 못했습니다.');
      setPersonas([]);
      setFacets([]);
      setPagination({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  }, [activeFilter, debouncedQuery, page]);

  useEffect(() => {
    load();
  }, [load]);

  const filters = useMemo(() => {
    const totalCount = facets.reduce((sum, item) => sum + Number(item.count || 0), 0);
    return [
      { key: FILTER_ALL, label: '전체', count: totalCount || pagination.total },
      ...facets.map((item) => ({
        key: item.tag,
        label: item.tag,
        count: item.count,
      })),
    ];
  }, [facets, pagination.total]);

  return (
    <B2BLayout hideCollapseButton={true} defaultCollapsed={false} hidePageHeader={true}>
      <B2BPageShell
        className="persona-explore-screen"
        title="Explore"
        description="제공되는 다양한 도메인의 퍼소나를 탐색하고, 바로 테스트에 활용해보세요."
        contentClassName="persona-explore-content"
      >
        <ErrorBanner message={error} />

        <label className="persona-explore-search">
          <Search size={18} strokeWidth={1.8} aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
            placeholder="이름, 태그, 직업으로 검색"
          />
        </label>

        <div className="persona-explore-filters" role="tablist" aria-label="Explore 필터">
          {filters.map((filter) => (
            <button
              key={filter.key}
              type="button"
              role="tab"
              aria-selected={activeFilter === filter.key}
              className={`persona-explore-filter ${activeFilter === filter.key ? 'is-active' : ''}`}
              onClick={() => {
                setActiveFilter(filter.key);
                setPage(1);
              }}
            >
              <span>{filter.label}</span>
              <em>{filter.count}</em>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="persona-explore-loading">
            <div className="persona-spinner" />
            <p>Explore 퍼소나를 불러오는 중입니다.</p>
          </div>
        ) : personas.length === 0 ? (
          <div className="persona-explore-empty">
            <Compass size={36} strokeWidth={1.5} />
            <h3>표시할 퍼소나가 없습니다</h3>
            <p>필터나 검색어를 바꿔 다시 탐색해 보세요.</p>
          </div>
        ) : (
          <>
            <div className="persona-explore-grid">
              {personas.map((persona) => {
                const profile = getExploreProfile(persona);
                const imageUrl = personaAssetUrl(persona.imageUrl || persona.image_url);
                const isAdopted = Boolean(persona.adoption?.adopted);
                const ageLabel = profile.age ? `${profile.age}세` : null;
                const meta = [profile.gender, ageLabel, profile.job].filter(Boolean).join(' · ');

                return (
                  <article key={persona.id} className="persona-explore-card">
                    <button
                      type="button"
                      className="persona-explore-card__hit"
                      onClick={() => navigate(`/personas/explore/${persona.id}`, {
                        state: {
                          from: {
                            pathname: '/personas/explore',
                            state: {
                              exploreState: {
                                activeFilter,
                                query,
                                page,
                              },
                            },
                          },
                        },
                      })}
                    >
                      <div className="persona-explore-card__body">
                        <div className="persona-explore-card__top">
                          <div className={`persona-explore-card__media ${imageUrl ? 'has-image' : ''}`}>
                            {imageUrl ? (
                              <img src={imageUrl} alt="" />
                            ) : (
                              <PersonaAvatar persona={persona} size="lg" />
                            )}
                          </div>
                          {profile.domainTag ? (
                            <span className="persona-explore-card__badge">{profile.domainTag}</span>
                          ) : null}
                        </div>

                        <div className="persona-explore-card__heading">
                          <div className="persona-explore-card__identity">
                            <h3>{persona.name || '이름 없는 퍼소나'}</h3>
                            {isAdopted ? (
                              <CheckCircle2
                                className="persona-explore-card__adopted-icon"
                                size={16}
                                strokeWidth={2.25}
                                aria-label="내 퍼소나에 담김"
                              />
                            ) : null}
                            <PersonaTagChip tag={profile.tag} />
                          </div>
                          {meta ? <p>{meta}</p> : null}
                        </div>
                        <p className="persona-explore-card__summary">
                          {profile.biography || profile.personality || profile.behavior || 'Explore seed'}
                        </p>
                        {(profile.notice.length > 0 || profile.domainHint) ? (
                          <ul className="persona-explore-card__signals">
                            {profile.domainHint ? <li>{profile.domainHint}</li> : null}
                            {profile.notice.map((item) => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    </button>
                  </article>
                );
              })}
            </div>
            <ExplorePagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onChange={setPage}
            />
          </>
        )}
      </B2BPageShell>
    </B2BLayout>
  );
}

export default ExplorePersonasPage;
